import { createFileRoute } from "@tanstack/react-router"

type RouteSearch = { page: number }

export const Route = createFileRoute("/app/duties/leave-requests/")({
  component: ReviewLeaveRequestsPage,
  beforeLoad: (context) => {
    return {
      getTitle: () => context.context.ts.absences.t("LeaveRequests_PageTitle"),
    }
  },
  validateSearch: (search: Record<string, unknown>): RouteSearch => {
    return { page: Number(search.page || 1) }
  },
})

import {
  SelectProps,
  Select,
  Center,
  Group,
  Button,
  Badge,
  Stack,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import dayjs from "dayjs"
import {
  MRT_ColumnDef,
  MRT_Row,
  useMantineReactTable,
  MantineReactTable,
} from "mantine-react-table"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { match } from "ts-pattern"
import { AbsentApprovalStatusBadge } from "../../../../components/absences/absent-approval-status-badge"
import { AbsentApprovalStatusIcon } from "../../../../components/absences/absent-approval-status-icon"
import { RenderPeriod } from "../../../../components/absences/render-period"
import { HideableFilterGroup } from "../../-components/hideable-filter-group"
import { useMrtLocalization } from "../../-components/use-mrt-localization"
import { Page } from "../../../../-components/Page"
import {
  GetMyApprovalsRequest,
  MyApprovalListItem,
  MyDecisionFilter,
  MyDecisionFilter_Case,
  StatusFilter,
  StatusFilter_Case,
  Sort,
  Sort_Case,
} from "../../../../@client/Theasoft_Absences_MyApprovals"
import { ErrorAlert } from "../../../../blocks/errors/error-alert"
import { RenderShiftType } from "../../../../blocks/shift-types/render-shift-type"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { useBreakpoints } from "../../../../utils/use-breakpoints"
import { useTranslations } from "../../../../utils/use-t"
import { useIsSmallScreen } from "../../../../utils/useIsSmallScreen"
import { InfinitePersonSingleSelect } from "../../../../-components/SelectFromQuery/InfinitePersonSingleSelect"
import { PersonName } from "@components/PersonName"

function DiscriminatedUnionSelect<Du, Value extends string>({
  map1,
  map2,
  value,
  onChange,
  data,
  ...rest
}: {
  map1: (du: Du) => Value
  map2: (value: Value) => Du
  value: Du | null
  data: { value: Value; label: string }[]
  onChange: (du: Du | null) => void
} & Omit<SelectProps, "data" | "value" | "onChange">) {
  return (
    <Select
      {...rest}
      value={value !== null ? map1(value) : null}
      data={data}
      onChange={(v) => {
        if (v !== null) {
          onChange(map2(v as Value))
        } else {
          onChange(null)
        }
      }}
    />
  )
}

const pageSize = 10

export function ReviewLeaveRequestsPage() {
  const search = Route.useSearch()

  const [filter, setFilter] = React.useState<GetMyApprovalsRequest>({
    page: search.page,
    myDecision: { Case: "All" },
    statusFilter: { Case: "All" },
    personId: null,
    sort: { Case: "RequestedAtDesc" },
  })
  const rawRequestParameters = React.useMemo(
    () => JSON.stringify({ ...filter, page: search.page }),
    [filter, search.page]
  )
  const { data, error } = useSuspenseQueryGet("/api/Absence/GetMyApprovals", {
    value: rawRequestParameters,
  })

  const isSmallScreen = useIsSmallScreen()
  const navigate = Route.useNavigate()
  const ts = useTranslations()
  const { locale } = useMrtLocalization()
  const { t } = useTranslation("app_server")
  const layout = useBreakpoints()

  const columns = useMemo<MRT_ColumnDef<MyApprovalListItem>[]>(
    () => [
      {
        accessorkey: "result",
        header: t("Status"),
        size: isSmallScreen ? 25 : 100,
        Cell: ({
          row: {
            original: { result },
          },
        }) =>
          result ? (
            isSmallScreen ? (
              <Center>
                <AbsentApprovalStatusIcon size="sm" status={result} />
              </Center>
            ) : (
              <AbsentApprovalStatusBadge status={result} variant="light" />
            )
          ) : null,
      },
      {
        accessorKey: "RequestedBy",
        header: ts.appServer.t("RequestedBy"),
        Cell: ({
          row: {
            original: { personStatus },
          },
        }) => <PersonName personId={personStatus.personId} />,
      },
      {
        accessorKey: "Reason",
        header: ts.appServer.t("Reason"),
        size: 50,
        Cell: ({ row }) => (
          <RenderShiftType id={row.original.personStatus.shiftTypeId}>
            {(v) => v.name}
          </RenderShiftType>
        ),
      },
      {
        accessorKey: "TimePeriod",
        header: ts.appServer.t("TimePeriod"),
        Cell: ({ row }) => <RenderPeriod value={row.original.period} />,
      },
      ...(isSmallScreen === true
        ? []
        : [
            {
              accessorKey: "RequestedAt",
              header: ts.appServer.t("RequestedAt"),
              Cell: ({ row }: { row: MRT_Row<MyApprovalListItem> }) => (
                <Text c="dimmed">
                  {dayjs(row.original.createdAt).format("L")}
                </Text>
              ),
            },
            {
              accessorKey: "Note",
              header: ts.appServer.t("Note"),
              Cell: (element: { row: MRT_Row<MyApprovalListItem> }) => (
                <Text c="orange">
                  {element.row.original.personStatus.requesterNote}
                </Text>
              ),
            },
            {
              accessorKey: "MyDecision",
              header: ts.appServer.t("MyDecision"),
              Cell: (element: { row: MRT_Row<MyApprovalListItem> }) => (
                <Group>
                  {element.row.original.myStatus.map((v, index) =>
                    match(v)
                      .with("ActivePending", () => (
                        <Button
                          key={v + "-" + index}
                          fullWidth
                          size="xs"
                          color="pink"
                        >
                          {ts.appServer.t("ReviewNow")}
                        </Button>
                      ))
                      .with("Approved", () => (
                        <Badge
                          key={v + "-" + index}
                          fullWidth
                          color="green"
                          variant="light"
                        >
                          {ts.appServer.t("Approved")}
                        </Badge>
                      ))
                      .with("Rejected", () => (
                        <Badge
                          key={v + "-" + index}
                          fullWidth
                          color="red"
                          variant="light"
                        >
                          {ts.appServer.t("Rejected")}
                        </Badge>
                      ))
                      .with("Pending", () => (
                        <Badge
                          key={v + "-" + index}
                          fullWidth
                          color="blue"
                          variant="light"
                        >
                          {ts.appServer.t("Pending")}
                        </Badge>
                      ))
                      .otherwise(() => null)
                  )}
                </Group>
              ),
            },
          ]),
    ],
    [layout]
  )
  const pagination = { pageIndex: search.page - 1, pageSize }
  const table = useMantineReactTable({
    columns,
    data: data.items,
    rowCount: data.count,
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function" ? updater(pagination) : updater
      navigate({ search: { page: newState.pageIndex + 1 } })
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate({
          to: "/app/duties/leave-requests/$id",
          params: {
            id: row.original.personStatus.personStatusId.toString(),
          },
        })
      },
      style: { cursor: "pointer" },
    }),
    state: {
      density: "xs",
      pagination,
      showColumnFilters: true,
    },
    localization: locale,

    enablePagination: true,
    manualPagination: true,
    enableTableHead: true,
    enableSorting: false,
    enableFilters: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableRowVirtualization: false,
    enableDensityToggle: false,
    enableEditing: false,
    enableColumnFilters: false,
    enableHiding: false,
    mantineTableContainerProps: {
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    mantinePaperProps: {
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: true,
    },
  })
  const [filtersOpened, { toggle: toggleFilters }] = useDisclosure()
  const activeFilters = [
    filter.personId !== null,
    filter.myDecision.Case !== "All",
  ].filter((v) => v).length
  return (
    <Page
      buttonGroupProps={{
        children: (
          <HideableFilterGroup.Button
            activeFilters={activeFilters}
            visible={filtersOpened}
            onChange={toggleFilters}
          />
        ),
      }}
    >
      <Stack h="100%">
        <ErrorAlert error={error} />

        <HideableFilterGroup visible={filtersOpened}>
          <InfinitePersonSingleSelect
            label={ts.appServer.t("RequestedBy")}
            value={filter.personId ? filter.personId.toString() : undefined}
            onSelectionChange={(values) =>
              setFilter((filter) => ({
                ...filter,
                personId: values ? parseInt(values) : null,
              }))
            }
            filterOptions={{ personCategoryTypes: ["Person"] }}
          />
          <Select
            label={ts.appServer.t("MyDecision")}
            value={match<MyDecisionFilter, MyDecisionFilter_Case>(
              filter.myDecision
            )
              .with({ Case: "All" }, () => "All")
              .with({ Case: "Approved" }, () => "Approved")
              .with({ Case: "Pending" }, () => "Pending")
              .with(
                { Case: "PendingAndActiveOrInfo" },
                () => "PendingAndActiveOrInfo"
              )
              .with({ Case: "Rejected" }, () => "Rejected")
              .exhaustive()}
            onChange={(v) => {
              setFilter((filter) => ({
                ...filter,
                myDecision: match<MyDecisionFilter_Case, MyDecisionFilter>(
                  v as MyDecisionFilter_Case
                )
                  .with("All", () => ({ Case: "All" }))
                  .with("Approved", () => ({ Case: "Approved" }))
                  .with("Pending", () => ({ Case: "Pending" }))
                  .with("PendingAndActiveOrInfo", () => ({
                    Case: "PendingAndActiveOrInfo",
                  }))
                  .with("Rejected", () => ({ Case: "Rejected" }))
                  .exhaustive(),
              }))
            }}
            data={[
              { value: "All", label: ts.absences.t("Filter_All") },
              { value: "Approved", label: ts.absences.t("Filter_Approved") },
              { value: "Pending", label: ts.absences.t("Filter_Pending") },
              {
                value: "PendingAndActiveOrInfo",
                label: ts.absences.t("Filter_Current"),
              },
              { value: "Rejected", label: ts.absences.t("Filter_Rejected") },
            ]}
          />
          <DiscriminatedUnionSelect<StatusFilter, StatusFilter_Case>
            label="Status"
            map1={(v) => v.Case}
            map2={(v) => ({ Case: v })}
            data={[
              { value: "All", label: ts.absences.t("Filter_All") },
              { value: "Rejected", label: ts.absences.t("Filter_Rejected") },
              { value: "Approved", label: ts.absences.t("Filter_Approved") },
              { value: "Pending", label: ts.absences.t("Filter_Pending") },
            ]}
            onChange={(v) => {
              if (v !== null) {
                setFilter((filter) => ({ ...filter, statusFilter: v }))
              }
            }}
            value={filter.statusFilter}
          />
          <DiscriminatedUnionSelect<Sort, Sort_Case>
            label={ts.appServer.t("Sort")}
            map1={(v) => v.Case}
            map2={(v) => ({ Case: v })}
            data={[
              {
                value: "RequestedAtDesc",
                label: ts.absences.t("Sort_RequestedAtDesc"),
              },
              {
                value: "RequestedAtAsc",
                label: ts.absences.t("Sort_RequestedAtAsc"),
              },
            ]}
            onChange={(v) => {
              if (v !== null) {
                setFilter((filter) => ({ ...filter, sort: v }))
              }
            }}
            value={filter.sort}
          />
        </HideableFilterGroup>
        <MantineReactTable table={table} />
      </Stack>
    </Page>
  )
}
